import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import AgendaTimeSlot from "@/domain/entities/agenda/AgendaTimeSlot";
import Email from "@/domain/entities/emails/Email";
import AIResponse, {
    ComponentDataContentType,
    isAgendaItemEntity,
    isAgendaTimeSlotEntity,
    isEmailEntity,
    MoreDataCallbackResult,
    type ReasoningItem,
} from "@/domain/entities/vistassistant/AIResponse";
import ConversationMarker, {
    type ThreadItem,
} from "@/domain/entities/vistassistant/ConversationMarker";
import DeepAnalysisError from "@/domain/entities/vistassistant/DeepAnalysisError";
import DeepAnalysisResult from "@/domain/entities/vistassistant/DeepAnalysisResult";
import { TicketType } from "@/domain/entities/vistassistant/TicketTypes";
import type {
    InternalTicket,
    PendingTicketFormData,
    ReportBugData,
} from "@/domain/entities/vistassistant/TicketTypes";
import UserRequest from "@/domain/entities/vistassistant/UserRequest";
import { eventKeys } from "@/lib/eventKeys";
import { isSameCopy } from "@/lib/mailIdentity";
import { queryKeys } from "@/lib/queryKeys";
import { emailMapper } from "@/mappers/email/emailMapper";
import AIResponseMapper from "@/mappers/vistassistant/AIResponseMapper";
import deepAnalysisErrorMapper from "@/mappers/vistassistant/deepAnalysisErrorMapper";
import deepAnalysisResultMapper from "@/mappers/vistassistant/deepAnalysisResultMapper";
import restoredConversationMapper from "@/mappers/vistassistant/restoredConversationMapper";
import userRequestMapper from "@/mappers/vistassistant/userRequestMapper";
import {
    Mail,
    useSdk,
    type AnswerFeedback,
    type AssistantUsage,
    type ComponentData,
    type ConversationContextSize,
    type ConversationFact,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useAgentModel from "./assistant/useAgentModel";
import { useConversationsList } from "./assistant/useConversationHistory";
import {
    answerMailsListId,
    AssistantMailsCache,
    createAssistantMailsCache,
    removeAssistantMail,
    updateAssistantMail,
} from "./mails/assistantMailsCache";
import useFeatureFlag from "./useFeatureFlag";
import useGetEventBus from "./useGetEventBus";
import { useToast } from "./utils/useToast";

// v1 contract: a conversation is closed past MAX_EXCHANGES user requests, so the thread
// holds at most 8 user bubbles + 8 answers. Step-based contract: the SDK sends the whole
// conversation on every question, and its measured size (gauge) closes it instead; the user
// can compact it. An answer is 1 assistant bubble, or up to 3 on the step-based contract
// (one per block of the turn). A deep analysis is merged into the AIResponse it belongs to
// (see handleAnalysisAgentSuccess) instead of being appended, so it never takes a slot of
// its own.
const MAX_EXCHANGES = 8;
const MAX_BUBBLES_PER_ANSWER = 3;
// Step-based contract: the thread may hold more exchanges than MAX_EXCHANGES.
const MAX_DISPLAYED_EXCHANGES = 50;

// Safety net only: the limits above already keep the thread short, this guarantees the
// bubble invariant should another path ever append a bubble on its own. Does not affect
// the AI context, which is managed by the SDK.
const MAX_DISPLAYED_MESSAGES =
    MAX_DISPLAYED_EXCHANGES * (1 + MAX_BUBBLES_PER_ANSWER);

const trimMessages = (msgs: ThreadItem[]) => {
    if (msgs.length <= MAX_DISPLAYED_MESSAGES) return msgs;

    return msgs.slice(msgs.length - MAX_DISPLAYED_MESSAGES);
};

const useVistassistant = () => {
    const queryClient = useQueryClient();
    const { conversationManager } = useSdk();
    const { toast } = useToast();
    const { t } = useTranslation();
    const { isAssistantPromptEnabled } = useFeatureFlag();
    const [messages, setMessages] = useState<ThreadItem[]>([]);
    // Counted apart from `messages`: trimMessages can drop the oldest bubbles, so the
    // list length cannot be used to detect the limit.
    const [exchangeCount, setExchangeCount] = useState(0);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
    // Reasoning of the turn still running (step-based contract): cleared when it ends.
    const [pendingReasoning, setPendingReasoning] = useState<ReasoningItem[]>(
        [],
    );
    const { agentModel, setAgentModel } = useAgentModel();
    const { refresh: refreshConversations, showInHistory } =
        useConversationsList();

    // Step-based contract: the thumb given under each answer, by turn.
    const [answerFeedback, setAnswerFeedback] = useState<
        Record<string, AnswerFeedback>
    >({});

    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const appendToThread = (item: ThreadItem) =>
        setMessages((prev) => trimMessages([...prev, item]));

    // Each block of the answer to `userRequestId`, replaced by `patch`.
    const patchAnswer = (
        userRequestId: string,
        patch: (message: AIResponse) => AIResponse,
    ) =>
        setMessages((prev) =>
            prev.map((message) =>
                message instanceof AIResponse &&
                message.userRequestId === userRequestId
                    ? patch(message)
                    : message,
            ),
        );

    // Same cache key as the main list so useMailActions (reply/forward/delete) mutates it directly.
    const seedAssistantMailsCache = (
        userRequestId: string,
        emails: Email[],
    ) => {
        queryClient.setQueryData(
            queryKeys.mails.list(userRequestId),
            createAssistantMailsCache(emails),
        );
    };

    // Step-based contract: the conversation's size, measured by the SDK at the end of each turn
    // (unknown before the first turn and right after a compaction).
    const hasConversationMemory =
        conversationManager?.hasConversationMemory?.() ?? false;
    const [contextSize, setContextSize] = useState<
        ConversationContextSize | undefined
    >(() => conversationManager?.getContextSize?.());
    // What the assistant has consumed (this conversation, and the user's month), read back by the
    // SDK after each turn: unlike the size, it never goes down.
    const [usage, setUsage] = useState<AssistantUsage | undefined>(() =>
        conversationManager?.getUsage?.(),
    );
    // What the conversation has retained (« key = value »: a chosen contact, something the user
    // specified), kept by the SDK: it sends the whole list whenever it changes.
    const [facts, setFacts] = useState<ConversationFact[]>(
        () => conversationManager?.getFacts?.() ?? [],
    );
    const forgetFact = useCallback(
        (key: string) => conversationManager?.forgetFact?.(key),
        [conversationManager],
    );

    // « Compacter »: the SDK has the conversation summarised, the summary replaces it for the
    // assistant. The thread on screen stays as it is, with a marker where it was compacted and
    // what the assistant keeps in mind.
    const compactConversation = useMutation({
        mutationFn: async () => {
            if (!conversationManager?.compactConversation) return false;
            const result = await conversationManager.compactConversation();
            if (!result) {
                toast({
                    title: t("vistassistant.compactConversationFailed"),
                    variant: "error",
                });

                return false;
            }
            appendToThread(
                new ConversationMarker({
                    at: new Date(),
                    summary: result.summary,
                }),
            );

            return true;
        },
    });
    const isCompacting = compactConversation.isPending;

    const isConversationLimitReached = hasConversationMemory
        ? !!contextSize && contextSize.used >= contextSize.max
        : exchangeCount >= MAX_EXCHANGES;

    const canCompactConversation =
        hasConversationMemory &&
        !isSendingMessage &&
        !isCompacting &&
        (conversationManager?.canCompactConversation?.() ?? false);

    // Derived per bubble: one answer can carry several analyses, each one ends on its own.
    const isWaitingForDeepAnalysis = messages.some(
        (message) => message instanceof AIResponse && message.isAnalysisPending,
    );

    // Guards "Retry" against a double click: state updates only land on the next render.
    const isRequestingAnswerRef = useRef(false);
    // The last turn was answered (so kept in the history by the SDK).
    const isTurnKept = useRef(false);

    // An answer as the thread shows it; its mails are seeded where their lists read them.
    const toThreadAnswer = (response: ComponentData) => {
        const aiMessage = AIResponseMapper(response);
        if (aiMessage.content.contentType === ComponentDataContentType.Mails) {
            const emails = aiMessage.content.contentData as Email[];
            seedAssistantMailsCache(aiMessage.userRequestId, emails);
        }
        // The mails the answer cites, and the one its draft answers: their own list, apart from
        // the mails of an action.
        if (aiMessage.answer) {
            const cited = Object.values(aiMessage.answer.emails);
            const replyTo = aiMessage.answer.draft?.replyTo;
            seedAssistantMailsCache(
                answerMailsListId(aiMessage.userRequestId),
                replyTo && !cited.some((mail) => isSameCopy(mail, replyTo))
                    ? [...cited, replyTo]
                    : cited,
            );
        }

        return aiMessage;
    };

    // History (step-based contract): a past conversation comes back as it was shown, and the next
    // question goes on from it. False: it could not be read, nothing changed.
    const openConversation = async (id: string): Promise<boolean> => {
        const restored = await conversationManager?.openConversation?.(id);
        if (!restored) {
            toast({
                title: t("vistassistant.history.openFailed"),
                variant: "error",
            });

            return false;
        }
        setMessages(
            trimMessages(
                restoredConversationMapper(restored.items, toThreadAnswer),
            ),
        );
        setExchangeCount(
            restored.items.filter((item) => item.kind === "turn").length,
        );
        // The thumbs already given come back with the conversation.
        setAnswerFeedback(restored.feedback ?? {});
        setPendingReasoning([]);

        return true;
    };

    // Asks the SDK to answer `content`, whose user bubble is already in the thread. « stopped »: the
    // user stopped the turn, its question bubble is gone.
    const requestAnswer = async (
        content: string,
    ): Promise<"stopped" | undefined> => {
        isTurnKept.current = false;
        try {
            isRequestingAnswerRef.current = true;
            setIsSendingMessage(true);
            setPendingReasoning([]);

            // Check if assistant prompt is enabled
            if (!isAssistantPromptEnabled) {
                // Simulate natural response delay (1.5 seconds)
                const delay = 1500;
                await new Promise((resolve) => {
                    setTimeout(resolve, delay);
                });

                // Create a disabled response message
                const aiMessage = new AIResponse({
                    userRequestId: `ai-${Date.now()}`,
                    aiComment: t("vistassistant.featureDisabled"),
                    content: {
                        contentData: t(
                            "vistassistant.featureDisabledDescription",
                        ),
                        contentType: ComponentDataContentType.FallBackResponse,
                        contentAction: undefined,
                        upcomingAnalysis: false,
                    },
                    aiVocalText: `${t("vistassistant.featureDisabled")} ${t(
                        "vistassistant.featureDisabledDescription",
                    )}`,
                    deepAnalysis: {
                        result: undefined,
                        error: undefined,
                    },
                });

                appendToThread(aiMessage);
                setExchangeCount((prev) => prev + 1);

                return undefined;
            }

            const response =
                await conversationManager.handleSingleMessage(content);

            if (!response) {
                throw new Error(t("errors.generateAnswer"));
            }
            // Turn stopped (« Arrêter »): the SDK took the question out of the conversation, the
            // thread does too.
            if (response.stopped) {
                setMessages((prev) => {
                    const last = prev[prev.length - 1];

                    return last instanceof UserRequest &&
                        last.content === content
                        ? prev.slice(0, -1)
                        : prev;
                });

                return "stopped";
            }
            // Turn cancelled by « Nouvelle conversation »: nothing to show, nothing to count.
            if (response.cancelled) {
                return undefined;
            }

            const aiMessage = toThreadAnswer(response);
            appendToThread(aiMessage);
            // A failed turn (F-1.17) shows an error bubble but does not count towards
            // MAX_EXCHANGES: only an answered turn does.
            if (!aiMessage.errorCause) {
                setExchangeCount((prev) => prev + 1);
                isTurnKept.current = true;
            }

            return undefined;
        } catch (error) {
            console.error(error);
            toast({
                title: t("errors.global.title"),
                description: t("errors.global.description"),
                variant: "error",
            });

            return undefined;
        } finally {
            isRequestingAnswerRef.current = false;
            setIsSendingMessage(false);
            setPendingReasoning([]);
        }
    };

    const sendMessage = async (content: string) => {
        // The input is replaced by the reset panel once the limit is reached, but a
        // pendingQuery (assistantStore) can still auto-submit a message. A compaction replaces
        // the conversation the question would be sent with: it waits.
        if (isConversationLimitReached || isCompacting) return undefined;

        appendToThread(userRequestMapper(content));
        // History (step-based contract): the conversation shows at the top of the list at once.
        showInHistory(content);

        const outcome = await requestAnswer(content);
        // Not kept (failed, stopped): the list drops the line shown in advance.
        if (outcome === "stopped" || !isTurnKept.current)
            refreshConversations();

        return outcome;
    };

    // « Arrêter » while the assistant works: the running turn is dropped.
    const stopAnswer = () => {
        conversationManager?.stopTurn?.();
    };

    // Thumb under an answer: shown at once, taken back if it could not be recorded.
    const sendAnswerFeedback = async (
        turnId: string,
        feedback: AnswerFeedback,
    ) => {
        const previous = answerFeedback[turnId];
        if (!conversationManager?.sendAnswerFeedback || previous === feedback)
            return;
        setAnswerFeedback((prev) => ({ ...prev, [turnId]: feedback }));
        const recorded = await conversationManager.sendAnswerFeedback(
            turnId,
            feedback,
        );
        if (!recorded) {
            setAnswerFeedback((prev) => {
                const next = { ...prev };
                if (previous) next[turnId] = previous;
                else delete next[turnId];

                return next;
            });
        }
    };

    // "Retry" on the error bubble of a failed turn (F-1.17): the error bubble goes away and the
    // same question is sent again, without a second user bubble. The SDK has already removed the
    // failed question from its history.
    const retryLastMessage = async () => {
        if (isRequestingAnswerRef.current) return;

        const thread = messagesRef.current;
        const failedAnswer = thread[thread.length - 1];
        const failedRequest = thread[thread.length - 2];
        if (
            !(failedAnswer instanceof AIResponse) ||
            !failedAnswer.errorCause ||
            !(failedRequest instanceof UserRequest)
        ) {
            return;
        }

        setMessages((prev) =>
            prev.filter((message) => message !== failedAnswer),
        );
        await requestAnswer(failedRequest.content);
    };

    const handleMoreDataCallBack = async (
        callback: () => Promise<MoreDataCallbackResult>,
    ) => {
        if (!callback) return;
        setIsLoadingMoreData(true);
        try {
            const response = await callback();
            if (!response) return;

            // Mails (AB#1628): the SDK callback returns the FULL SET (replacement
            // semantics), which already re-contains the first page's mails. Compute it
            // ONCE here, reused for both the display AND the react-query cache — these two
            // sources must stay identical, otherwise the AB#1628 duplicate reappears.
            const fullEmails = response.content.filter(isEmailEntity);

            patchAnswer(response.userRequestId, (message) => {
                if (
                    message.content.contentType ===
                    ComponentDataContentType.FreeTimeSlots
                ) {
                    const combinedTimeSlots = [
                        ...(message.content.contentData as AgendaTimeSlot[]),
                        ...response.content.filter(isAgendaTimeSlotEntity),
                    ];

                    return new AIResponse({
                        ...message,
                        content: {
                            ...message.content,
                            contentData: combinedTimeSlots,
                            moreDataCallBack: response.moreDataCallBack,
                        },
                    });
                }

                if (
                    message.content.contentType ===
                    ComponentDataContentType.AgendaItems
                ) {
                    const combinedAgendaItems = [
                        ...(message.content.contentData as AgendaItem[]),
                        ...response.content.filter(isAgendaItemEntity),
                    ];

                    return new AIResponse({
                        ...message,
                        content: {
                            ...message.content,
                            contentData: combinedAgendaItems,
                            moreDataCallBack: response.moreDataCallBack,
                        },
                    });
                }

                return new AIResponse({
                    ...message,
                    content: {
                        ...message.content,
                        contentData: fullEmails,
                        moreDataCallBack: response.moreDataCallBack,
                    },
                });
            });

            queryClient.setQueryData<AssistantMailsCache>(
                queryKeys.mails.list(response.userRequestId),
                createAssistantMailsCache(fullEmails),
            );
        } catch (error) {
            console.error(error);
            toast({
                title: t("errors.global.title"),
                description: t("errors.global.description"),
                variant: "error",
            });
        } finally {
            // Always release the spinner, even if the callback (next-block network fetch) fails.
            setIsLoadingMoreData(false);
        }
    };

    // The SDK sends the analysis with the userRequestId of its answer (F-0.5).
    const handleAnalysisAgentSuccess = (analysisResult: DeepAnalysisResult) => {
        const result = deepAnalysisResultMapper(analysisResult);
        patchAnswer(
            result.userRequestId,
            (message) =>
                new AIResponse({
                    ...message,
                    deepAnalysis: { ...message.deepAnalysis, result },
                }),
        );
    };

    const handleAnalysisAgentError = (analysisError: DeepAnalysisError) => {
        console.error("Error from deep analysis agent :", analysisError);
        const error = deepAnalysisErrorMapper(analysisError);
        patchAnswer(
            error.userRequestId,
            (message) =>
                new AIResponse({
                    ...message,
                    deepAnalysis: { ...message.deepAnalysis, error },
                }),
        );
    };

    // Several lists show inline at once, so patch every Mails response's cache, not just one.
    const patchAssistantCaches = (
        patch: (
            cache: AssistantMailsCache | undefined,
        ) => AssistantMailsCache | undefined,
    ) => {
        messagesRef.current.forEach((message) => {
            if (
                !(message instanceof AIResponse) ||
                message.content.contentType !== ComponentDataContentType.Mails
            ) {
                return;
            }

            queryClient.setQueryData<AssistantMailsCache>(
                queryKeys.mails.list(message.userRequestId),
                (cache) => patch(cache),
            );
        });
    };

    const handleMailUpdate = (newMail: Mail) => {
        const updatedMail = emailMapper(newMail);
        patchAssistantCaches((cache) =>
            updateAssistantMail(cache, updatedMail),
        );
    };

    const handleMailDelete = ({
        uniqueMailvistaId,
    }: {
        uniqueMailvistaId: string;
    }) => {
        patchAssistantCaches((cache) =>
            removeAssistantMail(cache, uniqueMailvistaId),
        );
    };

    const { off: offMailUpdated } = useGetEventBus<Mail>(
        eventKeys.mails.updated,
        handleMailUpdate,
    );

    const { off: offMailDeleted } = useGetEventBus<{
        uniqueMailvistaId: string;
    }>(eventKeys.mails.deleted, handleMailDelete);

    const { off: offAnalysisAgentSuccess } = useGetEventBus<DeepAnalysisResult>(
        eventKeys.analysis.agentSuccess,
        handleAnalysisAgentSuccess,
    );

    const { off: offAnalysisAgentError } = useGetEventBus<DeepAnalysisError>(
        eventKeys.analysis.agentError,
        handleAnalysisAgentError,
    );

    // Shown while the turn runs; the final answer carries the whole reasoning anyway.
    const handleReasoningProgress = ({
        reasoning,
    }: {
        traceId: string;
        reasoning: ReasoningItem[];
    }) => {
        if (isRequestingAnswerRef.current) setPendingReasoning(reasoning);
    };

    const { off: offReasoningProgress } = useGetEventBus<{
        traceId: string;
        reasoning: ReasoningItem[];
    }>(eventKeys.assistant.reasoningProgress, handleReasoningProgress);

    // Step-based contract: the gauge follows each turn; `null` when the size is no longer known
    // (new conversation, compaction).
    const handleContextSize = (size: ConversationContextSize | null) => {
        setContextSize(size ?? undefined);
    };

    const { off: offContextSize } =
        useGetEventBus<ConversationContextSize | null>(
            eventKeys.assistant.contextSize,
            handleContextSize,
        );

    const handleUsage = (read: AssistantUsage | null) => {
        setUsage(read ?? undefined);
    };

    const { off: offUsage } = useGetEventBus<AssistantUsage | null>(
        eventKeys.assistant.usage,
        handleUsage,
    );

    const { off: offFacts } = useGetEventBus<ConversationFact[]>(
        eventKeys.assistant.facts,
        setFacts,
    );

    // History: a turn of this conversation is kept, the list follows.
    const { off: offConversationSaved } = useGetEventBus<{
        conversationId: string;
    }>(eventKeys.assistant.conversationSaved, refreshConversations);

    const resetMessageCacheFn = async () => {
        try {
            await conversationManager.resetSingleMessageCache();
        } catch (error) {
            console.error(error);
        }
    };

    const resetMessageCache = useMutation({
        mutationFn: resetMessageCacheFn,
    });

    useEffect(
        () => () => {
            resetMessageCache.mutate();
            offMailUpdated(eventKeys.mails.updated, handleMailUpdate);
            offMailDeleted(eventKeys.mails.deleted, handleMailDelete);
            offAnalysisAgentSuccess(
                eventKeys.analysis.agentSuccess,
                handleAnalysisAgentSuccess,
            );
            offAnalysisAgentError(
                eventKeys.analysis.agentError,
                handleAnalysisAgentError,
            );
            offReasoningProgress(
                eventKeys.assistant.reasoningProgress,
                handleReasoningProgress,
            );
            offContextSize(eventKeys.assistant.contextSize, handleContextSize);
            offUsage(eventKeys.assistant.usage, handleUsage);
            offFacts(eventKeys.assistant.facts, setFacts);
            offConversationSaved(
                eventKeys.assistant.conversationSaved,
                refreshConversations,
            );
        },
        [],
    );

    const resetConversationFn = async () => {
        try {
            await resetMessageCache.mutate();
            setMessages([]);
            setExchangeCount(0);
            setAnswerFeedback({});
        } catch (error) {
            console.error(error);
        }
    };

    const resetConversation = useMutation({
        mutationFn: resetConversationFn,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sdkConversationManager = conversationManager as any;

    const submitTicket = async (
        data: PendingTicketFormData,
        ticketType: TicketType,
    ) => {
        if (!sdkConversationManager?.submitTicket) return;
        const isBug = ticketType === TicketType.Bug;
        await sdkConversationManager.submitTicket({
            title: data.title,
            description: data.description,
            type: ticketType,
            feature: data.feature,
            ...(isBug &&
                (() => {
                    const bugData = data as Omit<
                        ReportBugData,
                        "similarTickets"
                    >;

                    return {
                        bugType: bugData.bugType,
                        context: bugData.context,
                        isCritical: bugData.isCritical,
                        aiConfidence: bugData.aiConfidence,
                    };
                })()),
        });
    };

    const voteTicket = async (ticketId: string) => {
        if (!sdkConversationManager?.voteTicket) return undefined;

        return sdkConversationManager.voteTicket(ticketId);
    };

    const getTickets = useCallback(async (): Promise<InternalTicket[]> => {
        if (!sdkConversationManager?.getTickets) return [];
        const result = await sdkConversationManager.getTickets();

        return (result as InternalTicket[]) ?? [];
    }, [sdkConversationManager]);

    const deleteTicket = async (ticketId: string): Promise<boolean> => {
        if (!sdkConversationManager?.deleteTicket) return false;

        return sdkConversationManager.deleteTicket(ticketId);
    };

    return {
        sendMessage,
        retryLastMessage,
        stopAnswer,
        answerFeedback,
        sendAnswerFeedback,
        openConversation,
        isSendingMessage,
        messages,
        pendingReasoning,
        isConversationLimitReached,
        hasConversationMemory,
        contextSize,
        usage,
        facts,
        forgetFact,
        agentModel,
        setAgentModel,
        canCompactConversation,
        compactConversation,
        isWaitingForDeepAnalysis,
        resetConversation,
        handleMoreDataCallBack,
        isLoadingMoreData,
        submitTicket,
        voteTicket,
        getTickets,
        deleteTicket,
    };
};

export default useVistassistant;
